<template>
  <div class="planned-leave-table">
    <div v-if="isNew" class="row row-edit">
      <EditPlannedLeaveRow
        :isNew="true"
        :leave="editing"
        :configuration="configuration"
        :month="month"
        :calendarOptions="calendarOptions"
        :typeOptions="workloadTaskOptions"
        @cancel="cancel"
        @save="save($event)"
      />
    </div>
    <template v-for="(row, index) in rowsInMonth">
      <div class="row" :class="{ 'row-edit': editing === row }" :key="index">
        <template v-if="editing !== row">
          <div class="row-data" data-testid="planned-leave-table-existing">
            <div>{{ formatDate(row.startDate) }}</div>
            <div>{{ formatDate(row.endDate) }}</div>
            <div>{{ formatPercentage(row.percentage) }}</div>
            <div>{{ formatList(row.calendars, "All Calendars") }}</div>
            <div>{{ formatList(row.types, "All Leave") }}</div>
          </div>
          <div class="row-actions">
            <AlButton
              @click="edit(row)"
              v-if="!isNew"
              data-testid="planned-leave-table-edit-existing"
              id="planned-leave-table-edit-existing"
              :disabled="!editingAllowed"
              :tooltip="
                editingAllowed
                  ? ''
                  : 'New adjustments cannot be made to months in the past'
              "
              ><i class="fa fa-edit"
            /></AlButton>
            <AlButton
              @click="remove(row)"
              v-if="!isNew"
              variant="warning"
              data-testid="planned-leave-table-delete-existing"
              id="planned-leave-table-delete-existing"
              ><i class="fa fa-trash-alt"
            /></AlButton>
          </div>
        </template>
        <template v-if="editing === row">
          <EditPlannedLeaveRow
            :isNew="false"
            :leave="editing"
            :configuration="configuration"
            :month="month"
            :calendarOptions="calendarOptions"
            :typeOptions="workloadTaskOptions"
            @cancel="cancel"
            @save="save($event, index)"
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { format, isSameMonth, parseISO } from "date-fns";
import { Translator, Calendar, WorkloadTasks } from "@agyletime/wfm-vue-common";
import { Al2Button as AlButton } from "@playvox/almendra-components";
import EditPlannedLeaveRow from "./EditPlannedLeaveRow.vue";
import { utcToZonedTime } from "date-fns-tz";

export default {
  name: "UnplannedLeaveTable",
  mixins: [Translator],
  components: {
    EditPlannedLeaveRow,
    AlButton,
  },
  props: {
    configuration: {
      type: Object,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    month: {
      type: String,
      required: true,
    },
    isNew: {
      type: Boolean,
      default: false,
    },
    editingAllowed: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    isNew() {
      if (this.isNew) {
        this.editing = {};
      } else {
        this.editing = null;
      }
    },
    rows() {
      this.hydrateOptions();
    },
    $route() {
      this.editing = null;
    },
  },
  created() {
    this.getOptions();
  },
  data() {
    return {
      editing: null,
    };
  },
  computed: {
    rowsInMonth() {
      return this.rows
        .map((row, index) => ({
          ...row,
          index,
        }))
        .filter((row) => {
          const dateOne = utcToZonedTime(row.startDate, "UTC");
          const dateTwo = utcToZonedTime(new Date(this.month), "UTC");
          return isSameMonth(dateOne, dateTwo);
        });
    },
  },
  methods: {
    formatDate(value) {
      let dateToFormat = utcToZonedTime(value, "UTC");
      if (typeof dateToFormat === "string") {
        dateToFormat = parseISO(value);
      }
      return format(dateToFormat, "yyyy-MM-dd");
    },
    formatPercentage(value) {
      return `${value}%`;
    },
    formatList(value = [], defaultText = "All") {
      return value.map(this.formatName).join(", ") || defaultText;
    },
    formatName(value) {
      return value?.name || "";
    },
    edit(row) {
      this.editing = row;
    },
    remove(row) {
      const rows = [...this.rows];
      rows.splice(row.index, 1);
      this.$emit("save", rows);
    },
    cancel() {
      this.editing = null;
      this.$emit("cancel");
    },
    save(leave, index) {
      const rows = [...this.rows];
      if (this.isNew) {
        this.$emit("save", [leave, ...rows]);
      } else {
        rows.splice(index, 1, leave);
        this.$emit("save", rows);
      }
      this.editing = null;
    },
    async getOptions() {
      const [calendars, workloadTasks] = await Promise.all([
        Calendar.getCalendars(),
        WorkloadTasks.getWorkloadTasks(),
      ]);

      this.calendarOptions = calendars;
      this.workloadTaskOptions = workloadTasks.filter((task) => task.isLeave);

      this.hydrateOptions();
    },
    hydrateOptions() {
      this.rows.forEach((row) => {
        row.calendars = row.calendars.map((calendar) => {
          return (
            this.calendarOptions.find((option) => option._id == calendar._id) ||
            calendar
          );
        });
        row.types = row.types.map((type) => {
          return (
            this.workloadTaskOptions.find((option) => option.id == type.id) ||
            type
          );
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-planned-leave {
  max-width: 100%;
  width: 20rem;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
}

.row {
  padding: 1rem;
  border-bottom: 1px solid var(--color-eerie-black-40);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;

  &:not(.row-edit):hover {
    background-color: var(--color-playvox-green-10);

    .row-actions > * {
      opacity: 1;
      pointer-events: all;
    }
  }
}

.row-data {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;

  & > * {
    width: 20%;
  }
}

.row-actions > * {
  margin-right: 0.5rem;
  opacity: 0;
  pointer-events: none;
}

@media (max-width: 899px) {
  .row-data {
    min-width: 100%;
  }
  .row-data > * {
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 650px) {
  .row-data > * {
    min-width: 50%;
  }
  .row-actions > * {
    opacity: 1;
    pointer-events: all;
  }
}
</style>

<style>
.dr-input--sm.dr-input--has-icon .dr-input__append {
  padding-top: 3px;
}

.panel-header {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}
</style>
